import { ohnePersonendaten } from '../../../services/aiAssistContext';
import type { AsplanBundle } from '../types';
import { GEFAHRSTOFFE } from '../kataloge/gefahrstoffe';

/**
 * KI-Unterstuetzung fuer die Historische Erkundung.
 *
 * Prompt-Bau und Antwort-Auswertung sind reine Funktionen und ohne Netz
 * pruefbar; der Aufruf selbst steht in `geminiService.ts`.
 *
 * ═══ DIE GRENZE, DIE HIER GEZOGEN WIRD ════════════════════════════════════
 * Eine Historische Erkundung ermittelt TATSACHEN ueber ein bestimmtes
 * Grundstueck: wer dort wann was betrieben hat, welche Stoffe dabei
 * angefallen sind, wo Tanks und Leitungen lagen. Diese Tatsachen kennt ein
 * Sprachmodell nicht. Es kann sie nur erfinden — und eine erfundene
 * Nutzungsgeschichte ist gefaehrlicher als eine fehlende: sie begruendet
 * anschliessend die Auswahl der Analyseparameter, und dann trifft die
 * Analytik am tatsaechlichen Stoffspektrum vorbei, ohne dass es jemandem
 * auffaellt.
 *
 * Deshalb erarbeitet die KI hier NICHT die Geschichte des Grundstuecks. Sie
 * leistet vier Dinge, die sie kann:
 *
 *   1. ORDNEN — was in den Projektangaben schon steht (Bauvorhaben,
 *      Beschreibung, vorliegende Gutachten, bereits erfasste Befunde) zu
 *      einem lesbaren Kapitelentwurf zusammenfassen.
 *   2. FRAGEN — benennen, was fuer eine belastbare Erkundung noch fehlt, und
 *      wo es zu holen ist (Stadtarchiv, Altlastenkataster, historische
 *      Luftbilder, Bauakten, Kampfmitteldienst, Zeitzeugen).
 *   3. ABLEITEN — aus einer GENANNTEN Nutzung das zu erwartende
 *      Stoffspektrum. Das ist Fachwissen und kein Grundstueckswissen:
 *      Gaswerk → PAK, Cyanide, Ammonium; Galvanik → Chrom(VI), Cyanide;
 *      Tankstelle → MKW, BTEX.
 *   4. FOLGERN — welche Konsequenz aus den Luecken zu ziehen ist
 *      (Vorsorgeprinzip, erweiterte Analytik, vorlaufende Sondierung).
 *
 * Was dabei herauskommt, ist ein VORSCHLAG. Er wird nie selbsttaetig
 * gespeichert, und jede Aussage, die auf einer Annahme beruht, muss als
 * solche gekennzeichnet sein — dafuer sorgt das Antwortschema.
 */

// ═══════════════════════════════════════════════════════════════════════════
// EINGABE
// ═══════════════════════════════════════════════════════════════════════════

export interface ErkundungsEingabe {
    projektName: string;
    projektNummer?: string;
    adresse?: string;
    /** Was der Bearbeiter selbst schon weiss — der wichtigste Teil. */
    bekannt: string;
    /** Bauvorhaben und geplante Arbeiten, soweit im Plan erfasst. */
    bauvorhaben?: string;
    /** Titel der vorliegenden Gutachten und Berichte. */
    unterlagen: string[];
    /** Stoffe, die in den Proben bereits nachgewiesen sind. */
    nachgewiesen: string[];
}

/**
 * Die Beschreibung des Bauvorhabens aus `asplaene.objekt` — getrimmt, leer,
 * wenn keine erfasst ist. Die EINE Lesestelle fuer das Feld: der Reiter
 * „Grundlagen" zeigt es damit an, die Erkundung reicht es weiter.
 */
export const objektBeschreibung = (objekt: Record<string, unknown> | null | undefined): string => {
    const v = objekt?.beschreibung;
    return typeof v === 'string' ? v.trim() : '';
};

/**
 * Zieht aus dem Buendel zusammen, was die KI kennen darf.
 *
 * Die Beschreibung des Bauvorhabens geht durch `ohnePersonendaten`, bevor
 * sie den Prompt erreicht: sie ist seit dem Reiter „Grundlagen" ein frei
 * beschreibbares Feld und kann per Klick aus der Baubeschreibung der
 * Planungsphase kommen — dieselbe Quelle, die `aiAssistContext` fuer die
 * anderen Assistenten ebenfalls nur gefiltert weitergibt. Der Filter ist
 * grob und faengt Rufnummern, Mailadressen und Namen mit Anrede oder Rolle;
 * Anschriften faengt er nicht (Begruendung dort). Nutzungsgeschichte und
 * Ortsangabe bleiben ungefiltert — fuer eine Historische Erkundung sind sie
 * der Gegenstand, nicht Beiwerk.
 */
export const eingabeAusBundle = (
    b: AsplanBundle,
    projektName: string,
    projektNummer?: string,
    adresse?: string,
): ErkundungsEingabe => {
    const he = b.plan.historische_erkundung ?? {};

    const nachgewiesen = new Set<string>();
    for (const p of b.proben) {
        for (const bef of p.befunde) {
            if (bef.medium === 'feststoff' && bef.wert !== null) {
                nachgewiesen.add(bef.bezeichnung || bef.stoff_key);
            }
        }
    }

    return {
        projektName,
        projektNummer,
        adresse,
        bekannt: String(he.nutzungsgeschichte ?? '').trim(),
        bauvorhaben: ohnePersonendaten(objektBeschreibung(b.plan.objekt)).trim() || undefined,
        unterlagen: b.unterlagen.map(u => [u.herausgeber, u.bezeichnung].filter(Boolean).join(': ')),
        nachgewiesen: [...nachgewiesen],
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// ANTWORT
// ═══════════════════════════════════════════════════════════════════════════

/** Ein Punkt, den nur eine Recherche klaeren kann. */
export interface OffeneFrage {
    frage: string;
    /** Wo die Antwort zu holen ist. */
    quelle: string;
}

/** Ein Stoff, der aus einer genannten Nutzung folgt. */
export interface ErwarteterStoff {
    stoff: string;
    /** Welche Nutzung ihn erwarten laesst — ohne Herleitung kein Eintrag. */
    herleitung: string;
    /** Schluessel aus dem Gefahrstoffkatalog, falls zuzuordnen. */
    katalog_key?: string;
}

export interface ErkundungsVorschlag {
    /**
     * Entwurf der Nutzungsgeschichte — ausschliesslich aus dem, was der
     * Bearbeiter angegeben hat. Leer, wenn er nichts angegeben hat.
     */
    nutzungsgeschichte: string;
    /** Was ohne Recherche nicht zu beantworten ist. */
    offene_fragen: OffeneFrage[];
    /** Zu erwartende Stoffe mit Herleitung. */
    erwartete_stoffe: ErwarteterStoff[];
    /** Gebaeudeschadstoffe, die bei Bestand zu pruefen sind. */
    gebaeudeschadstoffe: string[];
    /** Vorschlag fuer das Feld „Was nicht ermittelbar war". */
    luecken: string;
    /** Vorschlag fuer das Feld „Welche Folge daraus gezogen wird". */
    luecken_folge: string;
    /** Hinweise des Modells auf eigene Unsicherheit. */
    hinweise: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// PROMPT
// ═══════════════════════════════════════════════════════════════════════════

const KATALOG_STOFFE = GEFAHRSTOFFE.map(s => `${s.key} = ${s.name}`).join('; ');

/**
 * Baut den Prompt.
 *
 * Rein und ohne Netz — damit im Test steht, was das Modell zu sehen bekommt.
 * Das ist bei einem Thema, bei dem Erfindungen schaden, kein Luxus: die
 * Verbote im Prompt sind Teil der Fachlichkeit und gehoeren geprueft.
 */
export const baueErkundungsPrompt = (e: ErkundungsEingabe): string => {
    const zeilen: string[] = [];

    zeilen.push(
        'Du unterstützt einen Koordinator nach DGUV Regel 101-004 bei der Historischen',
        'Erkundung (Anhang 3 Nr. 2) für eine Baustelle in einem kontaminierten Bereich.',
        '',
        'ABSOLUTE REGELN:',
        '1. Erfinde KEINE Tatsachen über dieses Grundstück. Weder frühere Betriebe noch',
        '   Jahreszahlen, Firmennamen, Tanklagen oder Schadensfälle. Wenn du etwas nicht',
        '   aus den unten stehenden Angaben weißt, gehört es in "offene_fragen".',
        '2. "nutzungsgeschichte" darf AUSSCHLIEẞLICH das zusammenfassen und ordnen, was',
        '   unter ANGABEN steht. Steht dort nichts, gib eine leere Zeichenkette zurück.',
        '3. "erwartete_stoffe" darfst du nur aus einer AUSDRÜCKLICH genannten Nutzung',
        '   ableiten. Trage die Herleitung mit ein ("Gaswerk -> PAK, Cyanide"). Ohne',
        '   genannte Nutzung bleibt die Liste leer.',
        '4. Schreibe auf Deutsch, sachlich, ohne Werbesprache und ohne Konjunktivketten.',
        // schreibregel: erlaubt (die Schreibregel nennt die verbotenen Zeichen beim Namen)
        '5. Schreibe ohne Gedankenstriche (– oder —); nutze Komma, Doppelpunkt oder einen neuen Satz. Schreibe Umlaute (ä, ö, ü, ß), keine Umschrift.',
        '',
        'ANGABEN:',
        `Bauvorhaben: ${e.projektName}${e.projektNummer ? ` (${e.projektNummer})` : ''}`,
    );

    if (e.adresse) zeilen.push(`Ort der Baustelle: ${e.adresse}`);
    if (e.bauvorhaben) zeilen.push(`Geplante Maßnahme: ${e.bauvorhaben}`);

    zeilen.push(
        '',
        'Was der Bearbeiter zur bisherigen Nutzung angegeben hat:',
        e.bekannt ? e.bekannt : '(nichts angegeben)',
    );

    if (e.unterlagen.length > 0) {
        zeilen.push('', 'Vorliegende Unterlagen:', ...e.unterlagen.map(u => `- ${u}`));
    }
    if (e.nachgewiesen.length > 0) {
        zeilen.push(
            '',
            'Bereits im Feststoff nachgewiesene Stoffe (aus den Proben dieses Plans):',
            ...e.nachgewiesen.map(s => `- ${s}`),
            'Diese Befunde sind Tatsachen. Du darfst sie verwenden, um Fragen nach der',
            'Herkunft zu formulieren, nicht, um eine Nutzung als gesichert darzustellen.',
        );
    }

    zeilen.push(
        '',
        'AUFGABE:',
        'a) Fasse die Angaben zu einem Entwurf der Nutzungsgeschichte zusammen.',
        'b) Benenne, was für eine belastbare Erkundung fehlt, und wo es zu holen ist',
        '   (z. B. Stadtarchiv, Altlastenkataster der Behörde, historische Luftbilder,',
        '   Bauakten, Kampfmitteldienst, Befragung von Zeitzeugen, Voreigentümer).',
        'c) Leite aus genannten Nutzungen das zu erwartende Stoffspektrum ab.',
        'd) Nenne Gebäudeschadstoffe, die bei Bestandsbauwerken zu prüfen sind.',
        'e) Formuliere Vorschläge für "Was nicht ermittelbar war" und "Welche Folge',
        '   daraus gezogen wird" (Vorsorgeprinzip, erweiterte Analytik, vorlaufende',
        '   Sondierung, Beweissicherung).',
        '',
        `Bekannte Stoffschlüssel für katalog_key: ${KATALOG_STOFFE}`,
        '',
        'Antworte als JSON-Objekt mit genau diesen Feldern:',
        '{',
        '  "nutzungsgeschichte": string,',
        '  "offene_fragen": [{ "frage": string, "quelle": string }],',
        '  "erwartete_stoffe": [{ "stoff": string, "herleitung": string, "katalog_key": string }],',
        '  "gebaeudeschadstoffe": [string],',
        '  "luecken": string,',
        '  "luecken_folge": string,',
        '  "hinweise": [string]',
        '}',
    );

    return zeilen.join('\n');
};

// ═══════════════════════════════════════════════════════════════════════════
// AUSWERTUNG
// ═══════════════════════════════════════════════════════════════════════════

const alsText = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

const alsTextliste = (v: unknown): string[] =>
    Array.isArray(v) ? v.map(alsText).filter(Boolean) : [];

/**
 * Liest die Antwort des Modells.
 *
 * Alles, was nicht der erwarteten Form entspricht, wird verworfen statt
 * durchgereicht: ein halb verstandener Vorschlag, der in ein Planfeld
 * wandert, ist schlimmer als gar keiner. Fehlende Felder ergeben leere
 * Listen — der Aufrufer sieht dann, dass nichts kam.
 */
export const leseErkundungsVorschlag = (roh: unknown): ErkundungsVorschlag => {
    const o = (roh && typeof roh === 'object' ? roh : {}) as Record<string, unknown>;

    const fragen: OffeneFrage[] = Array.isArray(o.offene_fragen)
        ? (o.offene_fragen as unknown[])
            .map(x => {
                const f = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
                return { frage: alsText(f.frage), quelle: alsText(f.quelle) };
            })
            .filter(f => f.frage)
        : [];

    const bekannteKeys = new Set(GEFAHRSTOFFE.map(s => s.key));
    const stoffe: ErwarteterStoff[] = Array.isArray(o.erwartete_stoffe)
        ? (o.erwartete_stoffe as unknown[])
            .map(x => {
                const s = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
                const key = alsText(s.katalog_key);
                return {
                    stoff: alsText(s.stoff),
                    herleitung: alsText(s.herleitung),
                    // Ein Schluessel, den der Katalog nicht kennt, wird
                    // weggelassen statt uebernommen — sonst zeigt der Plan
                    // spaeter auf einen Stoff, den es hier nicht gibt.
                    katalog_key: bekannteKeys.has(key) ? key : undefined,
                };
            })
            // Ohne Herleitung kein Eintrag: das ist die Regel aus dem Prompt,
            // und sie wird hier durchgesetzt und nicht erhofft.
            .filter(s => s.stoff && s.herleitung)
        : [];

    return {
        nutzungsgeschichte: alsText(o.nutzungsgeschichte),
        offene_fragen: fragen,
        erwartete_stoffe: stoffe,
        gebaeudeschadstoffe: alsTextliste(o.gebaeudeschadstoffe),
        luecken: alsText(o.luecken),
        luecken_folge: alsText(o.luecken_folge),
        hinweise: alsTextliste(o.hinweise),
    };
};

/**
 * Wandelt die offenen Fragen in den Text fuer das Feld „Was nicht ermittelbar
 * war".
 *
 * Bewusst zusammengesetzt und nicht vom Modell formuliert: so steht im Plan
 * genau das, was die Oberflaeche als offene Frage anzeigt — und niemand muss
 * zwei Fassungen desselben Gedankens abgleichen.
 */
export const lueckenText = (v: ErkundungsVorschlag): string => {
    if (v.luecken) return v.luecken;
    if (v.offene_fragen.length === 0) return '';
    return 'Nicht abschließend ermittelt: '
        + v.offene_fragen.map(f => f.frage.replace(/\?$/, '')).join('; ') + '.';
};

/** Ob der Vorschlag ueberhaupt etwas hergibt. */
export const istLeer = (v: ErkundungsVorschlag): boolean =>
    !v.nutzungsgeschichte
    && v.offene_fragen.length === 0
    && v.erwartete_stoffe.length === 0
    && v.gebaeudeschadstoffe.length === 0
    && !v.luecken
    && !v.luecken_folge;
